import { useMemo } from "react";
import { expandTransactionParts } from "../../utils/financeSplits";
import {
  UNCATEGORIZED_ID,
  partMatchesCategoryFilter,
} from "../../utils/financeCategoryFilter";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const TOTAL_ID = "__total__";
const FALLBACK_COLORS = [
  "#1976d2",
  "#c62828",
  "#2e7d32",
  "#6a1b9a",
  "#ef6c00",
  "#00838f",
  "#ad1457",
  "#4527a0",
  "#558b2f",
  "#00695c",
];

function isoDate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIso(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

function addDays(d, n) {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
}

function eachDay(fromIso, toIso) {
  const days = [];
  let cursor = parseIso(fromIso);
  const end = parseIso(toIso);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || cursor > end) {
    return days;
  }
  while (cursor <= end) {
    days.push(isoDate(cursor));
    cursor = addDays(cursor, 1);
  }
  return days;
}

function flattenCategories(tree, byId = new Map()) {
  (tree || []).forEach((node) => {
    byId.set(String(node.category_uuid), node);
    (node.children || []).forEach((child) => {
      byId.set(String(child.category_uuid), child);
    });
  });
  return byId;
}

function seriesForTransaction(tx, byId, selectedIds) {
  const focused = Boolean(selectedIds && selectedIds.length);
  if (!tx.category_uuid) {
    return {
      id: UNCATEGORIZED_ID,
      name: "Ukategoriseret",
      color: "#9e9e9e",
    };
  }
  const selfId = String(tx.category_uuid);
  const parentId = String(tx.parent_category_uuid || tx.category_uuid);
  const parent = byId.get(parentId);
  const self = byId.get(selfId);
  const exclude = Boolean(
    self?.exclude_from_totals ||
      parent?.exclude_from_totals ||
      self?.system_key === "internal_transfer" ||
      parent?.system_key === "internal_transfer"
  );
  let id = parentId;
  let name = tx.parent_category_name || parent?.name || tx.category_name || "Ukategoriseret";
  let color = parent?.color || self?.color || null;
  if (focused && selfId !== parentId) {
    id = selfId;
    name =
      tx.parent_category_name && tx.category_name
        ? `${tx.parent_category_name} / ${tx.category_name}`
        : tx.category_name || name;
    color = self?.color || parent?.color || null;
  }
  return { id, name, color, exclude };
}

function skipFromChart(tx, info, household, selectedIds) {
  if (!partMatchesCategoryFilter(tx, selectedIds)) return true;
  if (tx.is_internal_transfer || info.exclude) return true;
  if (!household && tx.exempt_from_budget) return true;
  return !(Number(tx.amount || 0) < 0);
}

export default function FinanceBurnupChart({
  transactions = [],
  categories = [],
  dateFrom,
  dateTo,
  household = false,
  formatMoney,
  selectedIds = [],
}) {
  const theme = useTheme();
  const todayIso = isoDate(new Date());

  const { data, series, showTotal } = useMemo(() => {
    const days = eachDay(dateFrom, dateTo);
    const byId = flattenCategories(categories);
    const totals = new Map();
    const meta = new Map();
    const daily = new Map();

    (transactions || []).flatMap(expandTransactionParts).forEach((tx) => {
      const amount = Number(tx.amount || 0);
      if (amount === 0) return;
      const info = seriesForTransaction(tx, byId, selectedIds);
      if (skipFromChart(tx, info, household, selectedIds)) return;
      const spent = Math.abs(amount);
      totals.set(info.id, (totals.get(info.id) || 0) + spent);
      if (!meta.has(info.id)) meta.set(info.id, info);
      const day = String(tx.booking_date).slice(0, 10);
      if (!daily.has(day)) daily.set(day, new Map());
      const bucket = daily.get(day);
      bucket.set(info.id, (bucket.get(info.id) || 0) + spent);
    });

    const ordered = [...meta.values()].sort(
      (a, b) => (totals.get(b.id) || 0) - (totals.get(a.id) || 0)
    );
    ordered.forEach((item, index) => {
      if (!item.color) item.color = FALLBACK_COLORS[index % FALLBACK_COLORS.length];
    });

    const cumulative = new Map(ordered.map((item) => [item.id, 0]));
    let total = 0;

    const rows = days.map((day) => {
      const increments = daily.get(day);
      if (increments) {
        increments.forEach((value, id) => {
          if (cumulative.has(id)) cumulative.set(id, cumulative.get(id) + value);
          total += value;
        });
      }
      const row = { date: day };
      const inActualRange = day <= todayIso;
      ordered.forEach((item) => {
        row[item.id] = inActualRange ? Math.round(cumulative.get(item.id) * 100) / 100 : null;
      });
      row[TOTAL_ID] = inActualRange ? Math.round(total * 100) / 100 : null;
      return row;
    });

    return {
      data: rows,
      series: ordered,
      showTotal: ordered.length > 0,
    };
  }, [transactions, categories, dateFrom, dateTo, household, todayIso, selectedIds]);

  if (!data.length || series.length === 0) {
    return (
      <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Ingen udgifter i perioden.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Udgifter per kategori
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Akkumuleret forbrug. Den sorte linje er total på tværs af kategorier.
      </Typography>
      <Box sx={{ width: "100%", height: 360 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              tickFormatter={(value) =>
                parseIso(value).toLocaleDateString("da-DK", { day: "numeric", month: "short" })
              }
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              tickFormatter={(value) =>
                Number(value || 0).toLocaleString("da-DK", { maximumFractionDigits: 0 })
              }
              width={72}
            />
            <Tooltip
              formatter={(value, name) => [
                typeof formatMoney === "function" ? formatMoney(value) : value,
                name,
              ]}
              labelFormatter={(label) => parseIso(label).toLocaleDateString("da-DK")}
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                borderColor: theme.palette.divider,
                borderRadius: 8,
              }}
            />
            <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
            {todayIso >= String(dateFrom).slice(0, 10) && todayIso <= String(dateTo).slice(0, 10) ? (
              <ReferenceLine
                x={todayIso}
                stroke={theme.palette.text.secondary}
                strokeDasharray="3 3"
                label={{ value: "I dag", position: "insideTopRight", fontSize: 11, fill: theme.palette.text.secondary }}
              />
            ) : null}
            {series.map((item) => (
              <Line
                key={item.id}
                type="linear"
                dataKey={item.id}
                name={item.name}
                stroke={item.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls={false}
              />
            ))}
            {showTotal ? (
              <Line
                type="linear"
                dataKey={TOTAL_ID}
                name="Total"
                stroke={theme.palette.text.primary}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 3 }}
                connectNulls={false}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
